import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import { Form, FormElement, FormPage, FormStatus } from '../types/formTypes';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../firebase/config';
import { 
  doc, 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { convertFirestoreTimestamps, saveFormToFirestore } from '../utils/firestore';

// Define the state type
export interface FormState {
  currentForm: Form | null;
  currentPageId: string | null;
  loading: boolean;
  error: string | null;
  isSaving: boolean;
  saveError: string | null;
  lastSaved: string | null;
  lastAddedPageId: string | null; // Track the last added page ID
  savedForms: Form[]; // Keep track of forms loaded from Firestore
  preventNavigation: boolean; // Flag to prevent unwanted navigation
}

// Define action types
type FormAction =
  | { type: 'CREATE_NEW_FORM'; payload: { title: string } }
  | { type: 'LOAD_FORM'; payload: { formId: string } }
  | { type: 'UPDATE_FORM'; payload: { form: Form } }
  | { type: 'ADD_PAGE'; payload: { element: FormElement } }
  | { type: 'UPDATE_PAGE'; payload: { pageId: string; element: FormElement } }
  | { type: 'DELETE_PAGE'; payload: { pageId: string } }
  | { type: 'REORDER_PAGE'; payload: { pageId: string; direction: 'up' | 'down' } }
  | { type: 'SET_CURRENT_PAGE'; payload: { pageId: string } }
  | { type: 'SAVE_FORM' }
  | { type: 'SAVE_FORM_SUCCESS'; payload: { form: Form } }
  | { type: 'SAVE_FORM_ERROR'; payload: { error: string } }
  | { type: 'LOAD_SAVED_FORMS'; payload: { forms: Form[] } }
  | { type: 'PUBLISH_FORM'; payload: { formId: string } }
  | { type: 'UNPUBLISH_FORM'; payload: { formId: string } }
  | { type: 'PREVENT_NAVIGATION' }
  | { type: 'ALLOW_NAVIGATION' };

// Create initial state
const initialFormState: FormState = {
  currentForm: null,
  currentPageId: null,
  loading: false,
  error: null,
  isSaving: false,
  saveError: null,
  lastSaved: null,
  lastAddedPageId: null,
  savedForms: [],
  preventNavigation: false
};

// Create the reducer
function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'CREATE_NEW_FORM': {
      const welcomePage: FormPage = {
        id: uuidv4(),
        element: {
          id: uuidv4(),
          type: 'welcome-screen',
          title: 'Welcome to my form',
          description: 'This is a description of my form',
          required: false,
          properties: {},
        },
      };

      // Add a default first question page with a short-text type
      const firstQuestionPage: FormPage = {
        id: uuidv4(),
        element: {
          id: uuidv4(),
          type: 'short-text',
          title: 'What is your name?',
          description: 'We want to get to know you better.',
          required: false,
          properties: {},
        },
      };

      const endPage: FormPage = {
        id: uuidv4(),
        element: {
          id: uuidv4(),
          type: 'end-screen',
          title: 'Thank you for completing the form!',
          description: 'Your response has been recorded.',
          required: false,
          properties: {},
        },
      };

      // Link welcome page to first question and first question to end page
      welcomePage.nextPageId = firstQuestionPage.id;
      firstQuestionPage.nextPageId = endPage.id;

      // We need to get the user ID from auth context, but it's passed from the provider
      // The actual user ID will be set in the form provider below
      const newForm: Form = {
        id: uuidv4(),
        userId: 'temp-user-id', // This will be replaced by the actual user ID in the FormProvider
        title: action.payload.title,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        status: 'draft', // New forms are always drafts
        pages: [welcomePage, firstQuestionPage, endPage],
        startPageId: welcomePage.id,
        settings: {
          theme: {
            primaryColor: '#6366F1',
            backgroundColor: '#FFFFFF',
            fontFamily: 'Inter, sans-serif',
          },
          showProgressBar: true,
        },
      };

      return {
        ...state,
        currentForm: newForm,
        currentPageId: welcomePage.id,
        isSaving: true,
      };
    }
    
    case 'LOAD_FORM': {
      // First try to find the form in the cached savedForms
      const form = state.savedForms.find((f) => f.id === action.payload.formId) || null;
      
      // Log information about the form being loaded
      //console.log(`Loading form with ID ${action.payload.formId}. Found: ${!!form}`);
      if (form) {
        //console.log(`Form has ${form.pages.length} pages. Start page: ${form.startPageId}`);
      }
      
      // If we're already viewing this form and just need to maintain state, keep the current page
      const shouldKeepCurrentPage = state.currentForm?.id === action.payload.formId && state.currentPageId;
      
      return {
        ...state,
        currentForm: form,
        // If we already have this form loaded with a specific page, maintain that page
        // Otherwise, default to the start page
        currentPageId: shouldKeepCurrentPage ? state.currentPageId : (form?.startPageId || null),
        loading: false,
        preventNavigation: false,
      };
    }
    
    case 'UPDATE_FORM': {
      //console.log("UPDATE_FORM action received:", action.payload.form);
      
      // Keep track of current page ID to preserve it
      const currentPageBeforeUpdate = state.currentPageId;
      
      // Check if this is a settings update with the preserveSettings flag
      const shouldPreserveSettings = action.payload.form.settings?.preserveSettings === true;
      //console.log("Should preserve settings:", shouldPreserveSettings);
      
      // Get the incoming settings
      const incomingTheme = action.payload.form.settings?.theme || {};
      
      // Find existing form to preserve status if available
      const existingForm = state.savedForms.find(f => f.id === action.payload.form.id);
      
      // Ensure the form has complete and proper settings structure, but respect incoming values
      const updatedForm = {
        ...action.payload.form,
        // CRITICAL: Preserve the published status if it exists in the current saved form
        status: action.payload.form.status || existingForm?.status || 'draft',
        // Also ensure we preserve publishedAt timestamp if it exists
        publishedAt: action.payload.form.publishedAt || existingForm?.publishedAt,
        settings: shouldPreserveSettings 
          ? {
              // For settings updates, keep the incoming settings exactly as they are
              ...action.payload.form.settings,
              // But remove the temporary flag
              preserveSettings: undefined,
            }
          : {
              // For regular updates, apply the defaults if needed
              ...(action.payload.form.settings || {}),
              showProgressBar: action.payload.form.settings?.showProgressBar !== false,
              theme: {
                ...(action.payload.form.settings?.theme || {}),
                // Only use defaults if values are missing - don't override existing values
                primaryColor: incomingTheme.primaryColor || '#6366F1',
                backgroundColor: incomingTheme.backgroundColor || '#FFFFFF',
                fontFamily: incomingTheme.fontFamily || 'Inter, sans-serif',
              },
            },
        updatedAt: Date.now(),
      };
      
      //console.log("Updated form with structured settings:", updatedForm);
      //console.log("Theme settings being applied:", updatedForm.settings.theme);
      //console.log("Preserved form status:", updatedForm.status);
      
      // Determine if form exists in savedForms or if it's a new form
      const existingFormIndex = state.savedForms.findIndex((f) => f.id === updatedForm.id);
      let updatedSavedForms;
      
      if (existingFormIndex >= 0) {
        // Update existing form in savedForms
        updatedSavedForms = [...state.savedForms];
        updatedSavedForms[existingFormIndex] = updatedForm;
      } else {
        // Add new form to savedForms
        updatedSavedForms = [...state.savedForms, updatedForm];
      }
      
      // Save to localStorage to persist changes
      localStorage.setItem('typeform_clone_forms', JSON.stringify(updatedSavedForms));
      
      // CRITICAL CHANGE: Save to Firestore in the background like UPDATE_PAGE
      setTimeout(() => {
        // Background save to Firestore without changing state
        saveFormToFirestore(updatedForm)
          .then(result => {
            if (result.success) {
              //console.log("Background save of form to Firestore successful");
            } else {
              //console.error("Background save of form to Firestore failed, but UI is unaffected");
            }
          })
          .catch(err => {
            console.error("Error in background save:", err);
          });
      }, 500);
      
      // Preserve the current page ID
      return {
        ...state,
        currentForm: updatedForm,
        savedForms: updatedSavedForms,
        // Explicitly maintain the same currentPageId
        currentPageId: currentPageBeforeUpdate,
        // Don't set isSaving: true to avoid triggering the save effect in FormProvider
      };
    }
    
    case 'ADD_PAGE': {
      if (!state.currentForm) return state;
      
      const newPageId = uuidv4();
      const addedElement: FormElement = {
        ...action.payload.element,
        id: uuidv4(),
      };
      
      //console.log(`*** ADDING NEW PAGE: ${newPageId} with element type: ${addedElement.type} ***`);
      
      // Create the new page object
      const newPage: FormPage = {
        id: newPageId,
        element: addedElement,
      };
      
      // Find the welcome screen and end screen
      const welcomeScreenIndex = state.currentForm.pages.findIndex(
        (page) => page.element.type === 'welcome-screen'
      );
      
      const endScreenIndex = state.currentForm.pages.findIndex(
        (page) => page.element.type === 'end-screen'
      );
      
      // Insert the new page before the end screen if it exists
      const updatedPages = [...state.currentForm.pages];
      if (endScreenIndex !== -1) {
        // Find the page before the end screen
        const pageBeforeEndScreenIndex = endScreenIndex - 1;
        const pageBeforeEndScreen = updatedPages[pageBeforeEndScreenIndex];
        
        // Insert the new page before the end screen
        updatedPages.splice(endScreenIndex, 0, newPage);
        
        // Update next page links
        if (pageBeforeEndScreen) {
          // The page before end screen should point to our new page
          updatedPages[pageBeforeEndScreenIndex] = {
            ...pageBeforeEndScreen,
            nextPageId: newPageId,
          };
          
          // Our new page should point to the end screen
          newPage.nextPageId = updatedPages[endScreenIndex + 1].id;
        }
      } else {
        // If no end screen, just add to the end and link from the last page
        if (updatedPages.length > 0) {
          const lastPage = updatedPages[updatedPages.length - 1];
          lastPage.nextPageId = newPageId;
        }
        updatedPages.push(newPage);
      }
      
      // Create updated form with the new page
      const updatedForm = {
        ...state.currentForm,
        pages: updatedPages,
        updatedAt: Date.now(),
      };
      
      //console.log(`*** ADDED NEW PAGE - setting currentPageId to: ${newPageId} ***`);
      
      // Update the form in Firestore
      saveFormToFirestore(updatedForm);
      
      // Determine if form exists in savedForms or if it's a new form
      const existingFormIndex = state.savedForms.findIndex((f) => f.id === updatedForm.id);
      let updatedSavedForms;
      
      if (existingFormIndex >= 0) {
        // Update existing form in savedForms
        updatedSavedForms = [...state.savedForms];
        updatedSavedForms[existingFormIndex] = updatedForm;
      } else {
        // Add new form to savedForms
        updatedSavedForms = [...state.savedForms, updatedForm];
      }
      
      // Return updated state with the new form and setting current page to the new page
      return {
        ...state,
        currentForm: updatedForm,
        currentPageId: newPageId, // Important: Set current page to the newly added page
        lastAddedPageId: newPageId, // Track the last added page ID
        savedForms: updatedSavedForms,
      };
    }
    
    case 'UPDATE_PAGE': {
      if (!state.currentForm) return state;
      
      //console.log(`Reducer UPDATE_PAGE for pageId: ${action.payload.pageId}, element:`, action.payload.element);
      //console.log(`Current page before update: ${state.currentPageId}`);

      // CRITICAL: Keep track of current page ID to preserve it
      const currentPageBeforeUpdate = state.currentPageId;

      // Create a new Form object with the updated page
      const updatedPages = state.currentForm.pages.map(page => {
        if (page.id === action.payload.pageId) {
          return {
            ...page,
            element: {
              ...action.payload.element,
            },
          };
        }
        return page;
      });
      
      const updatedForm = {
        ...state.currentForm,
        pages: updatedPages,
        updatedAt: Date.now(),
      };
      
      // Determine if form exists in savedForms or if it's a new form
      const existingFormIndex = state.savedForms.findIndex(f => f.id === updatedForm.id);
      let updatedSavedForms;
      
      if (existingFormIndex >= 0) {
        // Update existing form in savedForms
        updatedSavedForms = [...state.savedForms];
        updatedSavedForms[existingFormIndex] = updatedForm;
      } else {
        // Add new form to savedForms
        updatedSavedForms = [...state.savedForms, updatedForm];
      }
      
      // Save form to localStorage to persist changes
      localStorage.setItem('typeform_clone_forms', JSON.stringify(updatedSavedForms));
      
      //console.log(`UPDATE_PAGE: Preserving current page ID: ${currentPageBeforeUpdate}`);
      
      // CRITICAL CHANGE: Instead of setting isSaving to true which triggers a state update,
      // we'll manually save to Firestore in the background without affecting the UI state
      // This prevents any page jumps by keeping the current state stable
      setTimeout(() => {
        // Background save to Firestore without changing state
        saveFormToFirestore(updatedForm)
          .then(result => {
            if (result.success) {
              //console.log("Background save to Firestore successful");
            } else {
              //console.error("Background save to Firestore failed, but UI is unaffected");
            }
          })
          .catch(err => {
            console.error("Error in background save:", err);
          });
      }, 500);
      
      // Return updated state WITHOUT setting isSaving flag
      // This means we won't trigger the save effect in FormProvider
      return {
        ...state,
        currentForm: updatedForm,
        savedForms: updatedSavedForms,
        // CRITICAL: Maintain the same currentPageId - never change it during updates
        currentPageId: currentPageBeforeUpdate,
        // Don't set isSaving: true so we avoid the save effect in FormProvider
        // We're directly saving to Firestore in the background instead
      };
    }
    
    case 'DELETE_PAGE': {
      if (!state.currentForm) return state;
      
      //console.log(`Deleting page with ID: ${action.payload.pageId}`);
      
      // Find the page to delete
      const pageToDelete = state.currentForm.pages.find(p => p.id === action.payload.pageId);
      if (!pageToDelete) {
        //console.log(`Page with ID ${action.payload.pageId} not found for deletion`);
        return state;
      }
      
      // Find the page that points to the page to delete
      const previousPage = state.currentForm.pages.find(p => p.nextPageId === action.payload.pageId);
      //console.log(`Previous page that points to deleted page: ${previousPage?.id || 'None'}`);
      
      // Update the previous page to point to what the deleted page was pointing to
      const updatedPages = state.currentForm.pages.map(page => {
        if (page.id === previousPage?.id) {
          //console.log(`Updating previous page ${page.id} to point to ${pageToDelete.nextPageId || 'null'}`);
          return {
            ...page,
            nextPageId: pageToDelete.nextPageId,
          };
        }
        return page;
      }).filter(page => page.id !== action.payload.pageId);
      
      //console.log(`Updated pages after deletion: ${updatedPages.length} (was ${state.currentForm.pages.length})`);
      
      // Determine the new current page ID
      let newCurrentPageId = state.currentPageId;
      if (state.currentPageId === action.payload.pageId) {
        newCurrentPageId = previousPage?.id || state.currentForm.startPageId;
        //console.log(`Changing current page to: ${newCurrentPageId}`);
      }
      
      // Create the updated form
      const updatedForm = {
        ...state.currentForm,
        pages: updatedPages,
        updatedAt: Date.now(),
      };
      
      // Update the form in savedForms
      const existingFormIndex = state.savedForms.findIndex(f => f.id === updatedForm.id);
      let updatedSavedForms;
      
      if (existingFormIndex >= 0) {
        // Update existing form in savedForms
        updatedSavedForms = [...state.savedForms];
        updatedSavedForms[existingFormIndex] = updatedForm;
      } else {
        // Add new form to savedForms (shouldn't happen, but just in case)
        updatedSavedForms = [...state.savedForms, updatedForm];
      }
      
      // Save form to localStorage to persist changes
      localStorage.setItem('typeform_clone_forms', JSON.stringify(updatedSavedForms));
      
      //console.log(`Form with ID ${updatedForm.id} updated in savedForms and localStorage`);
      
      // Also save to Firestore
      saveFormToFirestore(updatedForm);
      
      return {
        ...state,
        currentForm: updatedForm,
        savedForms: updatedSavedForms,
        currentPageId: newCurrentPageId,
      };
    }
    
    case 'REORDER_PAGE': {
      if (!state.currentForm) return state;
      
      //console.log(`Reordering page with ID: ${action.payload.pageId}, direction: ${action.payload.direction}`);
      
      // Get the current pages
      const currentPages = [...state.currentForm.pages];
      
      // Find the index of the page to move
      const pageIndex = currentPages.findIndex(p => p.id === action.payload.pageId);
      if (pageIndex === -1) {
        //console.log(`Page with ID ${action.payload.pageId} not found for reordering`);
        return state;
      }
      
      // Get the page to move
      const pageToMove = currentPages[pageIndex];
      
      // Check if the page is a welcome or end screen, which should not be reordered
      if (pageToMove.element.type === 'welcome-screen' || pageToMove.element.type === 'end-screen') {
        //console.log(`Cannot reorder welcome or end screen: ${pageToMove.element.type}`);
        return state;
      }
      
      // Find the welcome screen and end screen indices to establish boundaries
      const welcomeIndex = currentPages.findIndex(p => p.element.type === 'welcome-screen');
      const endIndex = currentPages.findIndex(p => p.element.type === 'end-screen');
      
      let newIndex;
      if (action.payload.direction === 'up') {
        // Move up but not past welcome screen or first page
        newIndex = Math.max(welcomeIndex !== -1 ? welcomeIndex + 1 : 0, pageIndex - 1);
        // Don't move if already at the top
        if (newIndex === pageIndex) return state;
      } else {
        // Move down but not past end screen or last page
        newIndex = Math.min(endIndex !== -1 ? endIndex - 1 : currentPages.length - 1, pageIndex + 1);
        // Don't move if already at the bottom
        if (newIndex === pageIndex) return state;
      }
      
      // Remove the page from its current position
      currentPages.splice(pageIndex, 1);
      
      // Insert the page at the new position
      currentPages.splice(newIndex, 0, pageToMove);
      
      // Update the nextPageId connections to maintain flow
      for (let i = 0; i < currentPages.length - 1; i++) {
        currentPages[i] = {
          ...currentPages[i],
          nextPageId: currentPages[i + 1].id
        };
      }
      
      // The last page should not point to anything (unless it's the end-screen)
      if (currentPages.length > 0 && endIndex === -1) {
        currentPages[currentPages.length - 1] = {
          ...currentPages[currentPages.length - 1],
          nextPageId: undefined
        };
      }
      
      // Create the updated form
      const updatedForm = {
        ...state.currentForm,
        pages: currentPages,
        updatedAt: Date.now(),
      };
      
      // Update the form in savedForms
      const existingFormIndex = state.savedForms.findIndex(f => f.id === updatedForm.id);
      let updatedSavedForms;
      
      if (existingFormIndex >= 0) {
        // Update existing form in savedForms
        updatedSavedForms = [...state.savedForms];
        updatedSavedForms[existingFormIndex] = updatedForm;
      } else {
        // Add new form to savedForms
        updatedSavedForms = [...state.savedForms, updatedForm];
      }
      
      // Save to localStorage
      localStorage.setItem('typeform_clone_forms', JSON.stringify(updatedSavedForms));
      
      // Save to Firestore in the background
      saveFormToFirestore(updatedForm);
      
      return {
        ...state,
        currentForm: updatedForm,
        savedForms: updatedSavedForms,
        currentPageId: action.payload.pageId, // Keep the focus on the moved page
      };
    }
    
    case 'SET_CURRENT_PAGE': {
      // We're intentionally removing the navigation prevention check here
      // to ensure navigation sidebar clicks ALWAYS work
      
      // If trying to navigate to the same page, it's a no-op
      if (state.currentPageId === action.payload.pageId) {
        //console.log('Ignoring redundant page navigation');
        return state; // No changes needed
      }
      
      // Check if the page exists in the form
      const pageExists = state.currentForm?.pages.some(p => p.id === action.payload.pageId);
      if (!pageExists) {
        //console.log(`Cannot navigate to non-existent page: ${action.payload.pageId}`);
        return state;
      }
      
      //console.log(`Setting current page from ${state.currentPageId} to ${action.payload.pageId}`);
      
      // Always clear the navigation lock when directly navigating
      return {
        ...state,
        currentPageId: action.payload.pageId,
        lastAddedPageId: null, // Clear this flag once navigation completes
        preventNavigation: false // Always allow explicit navigation by user
      };
    }
    
    case 'SAVE_FORM': {
      if (!state.currentForm) return state;
      
      //console.log('SAVE_FORM action triggered - using background save approach');
      //console.log('Preserving currentPageId:', state.currentPageId);
      
      // Keep track of current page ID to preserve it
      const currentPageBeforeUpdate = state.currentPageId;
      const formToSave = state.currentForm; // Store reference to avoid null issues
      
      // CRITICAL CHANGE: Save to Firestore in the background
      setTimeout(() => {
        // Background save to Firestore without changing state
        saveFormToFirestore(formToSave)
          .then(result => {
            if (result.success) {
              //console.log("SAVE_FORM: Background save to Firestore successful");
              // We can't dispatch from here in the reducer, this will be handled by the component
              //console.log("Form saved successfully, component will handle UI updates");
            } else {
              //console.error("SAVE_FORM: Background save to Firestore failed");
              // We can't dispatch from here in the reducer, this will be handled by the component
              //console.error("Save failed, component will handle error state");
            }
          })
          .catch(err => {
            //console.error("Error in SAVE_FORM background save:", err);
            // We can't dispatch from here in the reducer, this will be handled by the component
            //console.error("Save error:", err.message || 'Unknown error');
          });
      }, 500);
      
      return {
        ...state,
        // Don't set isSaving to true, we're using background save
        // Ensure we don't lose the current page ID
        currentPageId: currentPageBeforeUpdate,
      };
    }
    
    case 'SAVE_FORM_SUCCESS': {
      const updatedForm = action.payload.form;
      const now = new Date().toLocaleTimeString();
      
      //console.log(`Form saved successfully at ${now}`);
      //console.log('CRITICAL: Ensuring currentPageId remains unchanged:', state.currentPageId);
      
      // Keep the original currentPageId
      const preservedPageId = state.currentPageId;
      
      // Ensure the updatedAt field is a JavaScript timestamp, not a Firestore timestamp
      // Also ensure the form status is preserved
      const normalizedForm = {
        ...updatedForm,
        // Ensure status is preserved
        status: updatedForm.status || state.currentForm?.status || 'draft',
        updatedAt: typeof updatedForm.updatedAt === 'object' && updatedForm.updatedAt !== null
          ? convertFirestoreTimestamps(updatedForm.updatedAt)
          : (updatedForm.updatedAt || Date.now())
      };
      
      //console.log('Form status after save:', normalizedForm.status);
      
      // Update the form in savedForms
      const existingFormIndex = state.savedForms.findIndex(form => form.id === normalizedForm.id);
      let updatedSavedForms = [...state.savedForms];
      
      if (existingFormIndex >= 0) {
        // Update existing form
        updatedSavedForms[existingFormIndex] = normalizedForm;
      } else {
        // Add as new form
        updatedSavedForms.push(normalizedForm);
      }
      
      //console.log('Before state update:', preservedPageId);
      
      return {
        ...state,
        savedForms: updatedSavedForms,
        currentForm: normalizedForm,
        currentPageId: preservedPageId, // Preserve the current page ID
        isSaving: false,
        lastSaved: now,
        preventNavigation: false // Clear the flag after save completes
      };
    }
    
    case 'SAVE_FORM_ERROR': {
      //console.log('Form save error:', action.payload.error);
      return {
        ...state,
        isSaving: false,
        saveError: action.payload.error,
        preventNavigation: false // Clear the flag after save fails
      };
    }
    
    case 'LOAD_SAVED_FORMS': {
      return {
        ...state,
        savedForms: action.payload.forms,
      };
    }
    
    case 'PUBLISH_FORM': {
      if (!state.currentForm) return state;
      
      // Find the form in savedForms
      const formToPublish = state.savedForms.find(form => form.id === action.payload.formId);
      if (!formToPublish) {
        console.error('PUBLISH_FORM: Form not found in savedForms:', action.payload.formId);
        return state;
      }
      
      //console.log('PUBLISH_FORM: Publishing form', action.payload.formId);
      
      // Ensure we preserve all form settings
      const publishedForm = {
        ...formToPublish,
        status: 'published' as FormStatus, // Explicitly set status to published
        publishedAt: Date.now(),
        updatedAt: Date.now(),
        // Preserve existing settings
        settings: {
          ...(formToPublish.settings || {}),
          preserveSettings: true // Mark for preservation during any subsequent saves
        }
      };
      
      // Log the status to verify it's correct
      //console.log('PUBLISH_FORM: Setting form status to:', publishedForm.status);
      //console.log('PUBLISH_FORM: Form has publishedAt timestamp:', publishedForm.publishedAt);
      
      // Update savedForms with the published form
      const updatedSavedForms = state.savedForms.map(form => 
        form.id === action.payload.formId ? publishedForm : form
      );
      
      //console.log('PUBLISH_FORM: Updated form status to published, triggering save');
      
      // Save to localStorage for immediate feedback
      localStorage.setItem('typeform_clone_forms', JSON.stringify(updatedSavedForms));
      
      // IMPORTANT: Force an immediate direct save to Firestore
      //console.log('PUBLISH_FORM: Forcing immediate save to Firestore with published status');
      
      // Create a direct reference to update status in Firestore
      const formRef = doc(db, 'forms', publishedForm.id);
      
      // First update just the status field to ensure it gets set
      setTimeout(async () => {
        try {
          //console.log('PUBLISH_FORM: Direct Firestore update of published status');
          await updateDoc(formRef, {
            status: 'published',
            publishedAt: Date.now(),
            updatedAt: serverTimestamp()
          });
          //console.log('PUBLISH_FORM: Successfully updated published status in Firestore');
          
          // Verify the status was properly set
          setTimeout(async () => {
            try {
              const verifyDoc = await getDoc(formRef);
              if (verifyDoc.exists()) {
                const verifyData = verifyDoc.data();
                if (verifyData.status !== 'published') {
                  //console.error('PUBLISH_FORM: Verification failed! Status is not published:', verifyData.status);
                  //console.log('PUBLISH_FORM: Attempting to fix published status...');
                  
                  // Try one more time to set it explicitly
                  await updateDoc(formRef, {
                    status: 'published',
                    publishedAt: Date.now()
                  });
                  
                  //console.log('PUBLISH_FORM: Status correction attempt completed');
                } else {
                  //console.log('PUBLISH_FORM: Verification successful! Status is published');
                }
              }
            } catch (verifyErr) {
              //console.error('PUBLISH_FORM: Error verifying status:', verifyErr);
            }
          }, 1000);
          
          // Then do a full save to ensure all data is in sync
          setTimeout(() => {
            saveFormToFirestore(publishedForm)
              .then(result => {
                if (result.success) {
                  //console.log('PUBLISH_FORM: Successfully saved complete published form to Firestore');
                  //console.log('PUBLISH_FORM: Status after save:', publishedForm.status);
                } else {
                  //console.error('PUBLISH_FORM: Failed to save published form to Firestore');
                }
              })
              .catch(err => {
                console.error('PUBLISH_FORM: Error saving published form to Firestore:', err);
              });
          }, 500);
        } catch (error) {
          console.error('PUBLISH_FORM: Error directly updating status in Firestore:', error);
        }
      }, 100);
      
      return {
        ...state,
        savedForms: updatedSavedForms,
        currentForm: state.currentForm.id === action.payload.formId ? publishedForm : state.currentForm,
        isSaving: true, // Trigger save effect
      };
    }
    
    case 'UNPUBLISH_FORM': {
      if (!state.currentForm) return state;
      
      // Find the form in savedForms
      const formToUnpublish = state.savedForms.find(form => form.id === action.payload.formId);
      if (!formToUnpublish) return state;
      
      // Update the form with draft status
      const unpublishedForm = {
        ...formToUnpublish,
        status: 'draft' as FormStatus,
        updatedAt: Date.now(),
      };
      
      // Update savedForms with the unpublished form
      const updatedSavedForms = state.savedForms.map(form => 
        form.id === action.payload.formId ? unpublishedForm : form
      );
      
      return {
        ...state,
        savedForms: updatedSavedForms,
        currentForm: state.currentForm.id === action.payload.formId ? unpublishedForm : state.currentForm,
        isSaving: true, // Trigger save effect
      };
    }
    
    case 'PREVENT_NAVIGATION': {
      //console.log('Setting preventNavigation flag to block unwanted navigation');
      return {
        ...state,
        preventNavigation: true
      };
    }
    
    case 'ALLOW_NAVIGATION': {
      //console.log('Clearing preventNavigation flag to allow navigation');
      return {
        ...state,
        preventNavigation: false
      };
    }
    
    default:
      return state;
  }
}

// Create context
interface FormContextType {
  state: FormState;
  dispatch: React.Dispatch<FormAction>;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

// Create provider component
interface FormProviderProps {
  children: ReactNode;
}

export const FormProvider: React.FC<FormProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(formReducer, initialFormState);
  const { currentUser } = useAuth();

  // Load user's forms from Firestore when user auth changes
  useEffect(() => {
    const loadUserForms = async () => {
      if (!currentUser) {
        dispatch({ type: 'LOAD_SAVED_FORMS', payload: { forms: [] } });
        return;
      }
      
      try {
        // Query forms for the current user
        const formsQuery = query(
          collection(db, 'forms'), 
          where('userId', '==', currentUser.uid)
        );
        
        const querySnapshot = await getDocs(formsQuery);
        const forms: Form[] = [];
        
        querySnapshot.forEach((doc) => {
          const formData = doc.data();
          // Convert any Firestore timestamps to regular JavaScript timestamps
          const processedFormData = convertFirestoreTimestamps(formData) as Form;
          forms.push(processedFormData);
        });
        
        //console.log('Loaded forms from Firebase:', forms.length);
        
        // Replace local storage forms with Firebase forms
        localStorage.setItem('typeform_clone_forms', JSON.stringify(forms));
        
        dispatch({ type: 'LOAD_SAVED_FORMS', payload: { forms } });
      } catch (error: any) {
        console.error('Error loading forms from Firestore:', error);
        
        // Handle permission errors specifically
        if (error.code === 'permission-denied') {
          console.warn('Firebase permission denied. Please check your Firestore security rules.');
          alert('Firebase permission denied. Please check the FIREBASE_SETUP.md file for instructions.');
        }
        
        // Fall back to any local forms
        const localForms = localStorage.getItem('typeform_clone_forms');
        if (localForms) {
          try {
            const parsed = JSON.parse(localForms);
            
            // Check if forms have user IDs and update them if needed
            const updatedForms = parsed.map((form: Form) => {
              if (!form.userId || form.userId === 'temp-user-id') {
                return { ...form, userId: currentUser.uid };
              }
              return form;
            });
            
            // Only keep forms that belong to the current user or have no user ID
            const filteredForms = updatedForms.filter((form: Form) => 
              !form.userId || form.userId === currentUser.uid
            );
            
            localStorage.setItem('typeform_clone_forms', JSON.stringify(filteredForms));
            dispatch({ type: 'LOAD_SAVED_FORMS', payload: { forms: filteredForms } });
          } catch (e) {
            console.error('Error parsing local forms:', e);
          }
        }
      }
    };
    
    loadUserForms();
  }, [currentUser]);
  
  // Handle form saving
  useEffect(() => {
    if (state.isSaving && state.currentForm) {
      //console.log('FormProvider: isSaving detected, initiating Firestore save process');
      //console.log('Current page ID being preserved:', state.currentPageId);
      
      // Store the current page ID to ensure it's not lost during async operations
      const pageIdToPreserve = state.currentPageId;
      
      // CRITICAL: Ensure we preserve the form's status (published/draft)
      // Check if this form is already marked as published in savedForms
      const existingFormInState = state.savedForms.find(f => f.id === state.currentForm?.id);
      const formStatus = existingFormInState?.status === 'published' 
        ? 'published' 
        : (state.currentForm.status || 'draft');
      
      //console.log('FormProvider: Preserving form status:', formStatus);
      //console.log('FormProvider: Published timestamp:', 
      //  existingFormInState?.publishedAt || state.currentForm.publishedAt || 'none');
      
      // Ensure the form has the current user ID and the correct status
      const formWithUserId = {
        ...state.currentForm,
        userId: currentUser?.uid || state.currentForm.userId,
        // Make sure the status is preserved with highest priority given to 'published'
        status: formStatus,
        // Also preserve publishedAt timestamp if exists
        publishedAt: state.currentForm.publishedAt || existingFormInState?.publishedAt,
      };
      
      // Save the form to Firestore
      const saveForm = async () => {
        try {
          //console.log('FormProvider: Starting save to Firestore for form:', formWithUserId.id);
          //console.log('FormProvider: Form status before save:', formWithUserId.status);
          
          // Save to Firestore directly - no need to pass currentPageId anymore
          const result = await saveFormToFirestore(formWithUserId);
          
          if (result.success) {
            //console.log('FormProvider: Form saved successfully to Firestore:', formWithUserId.id);
            //console.log('Preserving currentPageId:', pageIdToPreserve);
            //console.log('Form status after save:', formWithUserId.status);
            
            // Update the form with the success status
            dispatch({ 
              type: 'SAVE_FORM_SUCCESS', 
              payload: { form: formWithUserId }
            });
            
            // If the current page ID changed during save, force it back to what it was
            if (state.currentPageId !== pageIdToPreserve && pageIdToPreserve) {
              //console.log('FormProvider: Current page changed during save, forcing back to:', pageIdToPreserve);
              setTimeout(() => {
                dispatch({ type: 'SET_CURRENT_PAGE', payload: { pageId: pageIdToPreserve } });
              }, 100);
            }
          } else {
            console.error('FormProvider: Failed to save form to Firestore');
            dispatch({
              type: 'SAVE_FORM_ERROR',
              payload: { error: 'Failed to save form to Firestore' }
            });
          }
          
          // Also save to localStorage as a backup
          const localForms = localStorage.getItem('typeform_clone_forms');
          let updatedLocalForms = [];
          
          if (localForms) {
            try {
              const parsedForms = JSON.parse(localForms);
              const existingFormIndex = parsedForms.findIndex((f: Form) => f.id === formWithUserId.id);
              
              if (existingFormIndex >= 0) {
                parsedForms[existingFormIndex] = formWithUserId;
                updatedLocalForms = parsedForms;
              } else {
                updatedLocalForms = [...parsedForms, formWithUserId];
              }
            } catch (e) {
              updatedLocalForms = [formWithUserId];
            }
          } else {
            updatedLocalForms = [formWithUserId];
          }
          
          localStorage.setItem('typeform_clone_forms', JSON.stringify(updatedLocalForms));
          
          // Still dispatch success event since we saved locally
          dispatch({ 
            type: 'SAVE_FORM_SUCCESS', 
            payload: { form: formWithUserId }
          });
          
        } catch (error: any) {
          console.error('Error saving form to Firestore:', error);
          
          // Handle permission errors specifically
          if (error.code === 'permission-denied') {
            console.warn('Firebase permission denied. Please check your Firestore security rules.');
            alert('Firebase permission denied. Please check the FIREBASE_SETUP.md file for instructions.');
          } else {
            // Show specific error
            alert(`Error saving to Firestore: ${error.message}`);
          }
          
          // Also save to localStorage even if Firestore fails
          try {
            const localForms = localStorage.getItem('typeform_clone_forms');
            let updatedLocalForms = [];
            
            if (localForms) {
              const parsedForms = JSON.parse(localForms);
              const existingFormIndex = parsedForms.findIndex((f: Form) => f.id === formWithUserId.id);
              
              if (existingFormIndex >= 0) {
                parsedForms[existingFormIndex] = formWithUserId;
                updatedLocalForms = parsedForms;
              } else {
                updatedLocalForms = [...parsedForms, formWithUserId];
              }
            } else {
              updatedLocalForms = [formWithUserId];
            }
            
            localStorage.setItem('typeform_clone_forms', JSON.stringify(updatedLocalForms));
            
            // Still dispatch success event since we saved locally
            dispatch({ 
              type: 'SAVE_FORM_SUCCESS', 
              payload: { form: formWithUserId }
            });
            
          } catch (localError) {
            console.error('Error saving form to localStorage:', localError);
            dispatch({
              type: 'SAVE_FORM_ERROR',
              payload: { error: error.message || 'Failed to save form' }
            });
          }
        }
      };
      
      saveForm();
    }
  }, [state.isSaving, state.currentForm, currentUser, state.currentPageId]);
  
  // Handle create new form - ensure user ID is set
  useEffect(() => {
    // If we have a current form with a temporary user ID, update it
    if (
      state.currentForm && 
      currentUser && 
      state.currentForm.userId === 'temp-user-id'
    ) {
      const updatedForm = {
        ...state.currentForm,
        userId: currentUser.uid,
      };
      
      dispatch({ type: 'UPDATE_FORM', payload: { form: updatedForm } });
    }
  }, [state.currentForm, currentUser]);
  
  return (
    <FormContext.Provider value={{ state, dispatch }}>
      {children}
    </FormContext.Provider>
  );
};

// Create custom hook for using the context
export const useFormContext = () => {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
}; 